/**
 * User service for authentication and user management
 */

import type { QueryParams } from '@type/services';
import { BaseService } from './BaseService';

export class UserService extends BaseService {
  /**
   * Login with username and password
   * @param credentials - Login credentials
   * @returns Login response
   */
  async login(credentials: Record<string, unknown>) {
    return this.request<Record<string, unknown>>({
      method: 'POST',
      url: `${this.USER}/login`,
      data: credentials,
    });
  }

  /**
   * Logout current user
   */
  async logout() {
    return this.request<void>({
      method: 'POST',
      url: `${this.USER}/logout`,
    });
  }

  /**
   * Get current user information
   * @returns User information
   */
  async getAboutMe() {
    return this.request<{
      username?: string;
      tenantId?: string;
      tenantName?: string;
      roles?: string[];
      email?: string;
      userId?: string;
      userFullName?: string;
    }>({
      method: 'GET',
      url: `${this.USER}/about-me`,
    });
  }

  /**
   * Register a new user
   * @param userData - User registration data
   */
  async register(userData: Record<string, unknown>) {
    return this.request<void>({
      method: 'POST',
      url: `${this.USER}/register`,
      data: userData,
    });
  }

  /**
   * Modify an existing user
   * @param userData - User update data
   */
  async modifyUser(userData: Record<string, unknown>) {
    return this.request<void>({
      method: 'POST',
      url: `${this.USER}/modify-user`,
      data: userData,
    });
  }

  /**
   * Switch tenant for the logged-in session
   * @param tenantData - Tenant switch data
   * @returns Switch tenant response
   */
  async switchTenant(tenantData: Record<string, unknown>) {
    return this.request<Record<string, unknown>>({
      method: 'POST',
      url: `${this.USER}/switch-tenant`,
      data: tenantData,
    });
  }

  /**
   * Get all users (admin only)
   * @param params - Query parameters
   * @returns List of users
   */
  async findAll(params?: QueryParams) {
    return this.request<Record<string, unknown>[]>({
      method: 'GET',
      url: this.USER,
      params,
    });
  }

  /**
   * Update a user (admin only)
   * @param id - User ID
   * @param userData - User update data
   */
  async updateUser(id: string, userData: Record<string, unknown>) {
    return this.request<void>({
      method: 'PATCH',
      url: `${this.USER}/${id}`,
      data: userData,
    });
  }
}
